import React, {createRef, useCallback, useEffect, useState} from 'react';
import {
    IconButton,
    Button,
    ClickAwayListener,
    FormControl,
    FormHelperText,
    FormLabel,
    InputLabel,
    MenuItem,
    Select,
    TableCell,
    TableRow,
    TextField,
    Tooltip,
    Grid,
} from '@material-ui/core';
import withWidth, {isWidthUp} from '@material-ui/core/withWidth';
import {DatePicker} from '@material-ui/pickers';
import AddIcon from '@material-ui/icons/Add';
import get from 'lodash.get';
import {Controller, useFormContext} from 'react-hook-form';
import {FancyTable} from 'components';
import Show from 'components/utils/Show';

const TableInput = ({
    name,
    label,
    type: tankermanType,
    value = [],
    clearError,
    onError,
    onChange,
    width,
    ...props
}) => {
    // Input state
    const [date, setDate] = useState(null);
    const [type, setType] = useState('');
    const [signed, setSigned] = useState('');
    const [touched, setTouched] = useState(false);
    const [hasError, setHasError] = useState(false);
    const inputRow = createRef();
    const dateInput = createRef();
    const container = createRef();

    const isSmDown = !isWidthUp('sm', width);

    const handleAdd = useCallback(() => {
        if (date && type && signed !== '') {
            const newData = [
                ...value,
                {date, transfer_type: type, _signed: signed},
            ];
            onChange(newData);
            setDate(null);
            setType('');
            setSigned('');
        }
    }, [value, onChange, date, type, signed]);

    const handleClickAway = useCallback(() => {
        handleAdd();
    }, [handleAdd]);

    useEffect(() => {
        const inputRef = inputRow.current;
        const handleInput = e => {
            // Enter
            if (e.keyCode === 13) {
                handleClickAway();
            }
        };
        inputRef.addEventListener('keydown', handleInput);

        return () => {
            inputRef.removeEventListener('keydown', handleInput);
        };
    }, [handleClickAway, inputRow]);

    useEffect(() => {
        // Focus input when data changes
        if (dateInput.current && touched) {
            dateInput.current.querySelector('input').focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const firstClick = useCallback(
        e => {
            if (!value.length && !touched) {
                dateInput.current?.querySelector('input').focus();
            }
        },
        [value, touched, dateInput]
    );

    useEffect(() => {
        const containerRef = container.current;
        containerRef?.addEventListener('click', firstClick);
        return () => {
            containerRef?.removeEventListener('click', firstClick);
        };
    }, [container, firstClick]);

    const dateComponent = (
        <DatePicker
            fullWidth
            disableFuture
            autoOk
            allowKeyboardControl
            inputFormat='MM/DD/YYYY'
            label={`${tankermanType} Completed`}
            views={['year', 'month', 'date']}
            renderInput={({helperText, ...p}) => (
                <TextField
                    {...p}
                    fullWidth
                    ref={dateInput}
                    onFocus={() => setTouched(true)}
                    onBlur={handleAdd}
                    inputProps={{
                        ...p.inputProps,
                        'data-cy': 'tankerman-date-input',
                    }}
                />
            )}
            value={date}
            onChange={moment => {
                if (moment.isValid()) {
                    setDate(moment?.format('YYYY-MM-DD'));
                    setHasError(false);
                    clearError();
                } else {
                    setHasError(true);
                    onError('invalidDate');
                }
            }}
            onError={e => {
                setHasError(true);
                onError(e);
            }}
        />
    );

    const typeComponent = (
        <FormControl variant='standard' fullWidth>
            <InputLabel>Transfer Type</InputLabel>
            <Select
                name='tankerman-type'
                label='Transfer Type'
                onChange={e => setType(e.target.value)}
                value={type}
                onFocus={() => setTouched(true)}
                onBlur={handleAdd}
            >
                <MenuItem value=''>
                    <em>Choose one</em>
                </MenuItem>
                <MenuItem value='Dangerous Liquid'>Dangerous Liquids</MenuItem>
                <MenuItem value='Liquefied Gas'>Liquefied Gasses</MenuItem>
            </Select>
        </FormControl>
    );

    const signedComponent = (
        <FormControl variant='standard' fullWidth>
            <InputLabel>Signed</InputLabel>
            <Select
                name='signed'
                label='Signed'
                onChange={e => {
                    if (e.target.value === false) {
                        onError('notSigned');
                        setHasError(true);
                    } else {
                        clearError();
                        setHasError(false);
                    }
                    setSigned(e.target.value);
                }}
                value={signed}
                onFocus={() => setTouched(true)}
                onBlur={handleAdd}
            >
                <MenuItem value=''>
                    <em>--</em>
                </MenuItem>
                <MenuItem value={true}>Yes</MenuItem>
                <MenuItem value={false}>No</MenuItem>
            </Select>
        </FormControl>
    );

    return (
        <ClickAwayListener onClickAway={handleAdd} mouseEvent='onMouseDown'>
            <FancyTable
                {...props}
                title={
                    <FormLabel id={`${name}-label`} required>
                        {label}
                    </FormLabel>
                }
                value={value}
                headers={[
                    {label: 'Date', key: 'date', type: 'date'},
                    {label: 'Type', key: 'transfer_type'},
                    {label: 'Signed', key: '_signed'},
                ]}
                ref={container}
                onChange={data => {
                    onChange(data);
                }}
            >
                <TableRow ref={inputRow}>
                    <TableCell>
                        <Show condition={!isSmDown}>
                            <Tooltip title='Add'>
                                <Button
                                    variant='filled'
                                    color='secondary'
                                    startIcon={<AddIcon />}
                                    size='small'
                                    data-cy='tankerman-add'
                                    disabled={
                                        !date ||
                                        !type ||
                                        signed === '' ||
                                        hasError
                                    }
                                    onClick={handleAdd}
                                >
                                    Add
                                </Button>
                            </Tooltip>
                        </Show>
                        <Show condition={isSmDown}>
                            <Tooltip title='Add'>
                                <IconButton
                                    edge='start'
                                    variant='contained'
                                    color='secondary'
                                    data-cy='tankerman-add'
                                    disabled={
                                        !date ||
                                        !type ||
                                        signed === '' ||
                                        hasError
                                    }
                                    onClick={handleAdd}
                                >
                                    <AddIcon />
                                </IconButton>
                            </Tooltip>
                        </Show>
                    </TableCell>
                    <Show condition={!isSmDown}>
                        <TableCell align='right' style={{width: '40%'}}>
                            {dateComponent}
                        </TableCell>
                        <TableCell align='right' style={{width: '40%'}}>
                            {typeComponent}
                        </TableCell>
                        <TableCell align='right' style={{width: '20%'}}>
                            {signedComponent}
                        </TableCell>
                    </Show>
                    <Show condition={isSmDown}>
                        <TableCell colSpan={3}>
                            <Grid
                                container
                                spacing={1}
                                direction='column'
                                justifyContent='stretch'
                                alignItems='stretch'
                            >
                                <Grid item>{dateComponent}</Grid>
                                <Grid item>{typeComponent}</Grid>
                                <Grid item>{signedComponent}</Grid>
                            </Grid>
                        </TableCell>
                    </Show>
                </TableRow>
            </FancyTable>
        </ClickAwayListener>
    );
};

const TankermanTableField = ({label, name, required, type, width}) => {
    const {control, errors, clearErrors, setError} = useFormContext();
    const error = get(errors, name);
    const customErrors = e => {
        if (e === 'invalidDate') {
            setError(name, {type: 'dateInvalid', message: 'Date is not valid'});
        } else if (e === 'notSigned') {
            setError(name, {
                type: 'notSigned',
                message: 'Signatures are required for valid entries',
            });
        }
    };

    return (
        <FormControl error={!!error} style={{width: '100%'}}>
            <Controller
                render={p => (
                    <TableInput
                        width={width}
                        label={label}
                        name={p.name}
                        type={type}
                        error={!!error}
                        onBlur={p.onBlur}
                        onChange={p.onChange}
                        value={p.value}
                        onError={customErrors}
                        clearError={clearErrors}
                    />
                )}
                name={name}
                control={control}
            />
            <FormHelperText error>{error?.message}</FormHelperText>
        </FormControl>
    );
};

export default withWidth()(TankermanTableField);
